// @copyright  2006-2026 West Indies Software Engineering Company Limited. All rights reserved.

export default {
    '$dir': 'ltr',

    'practiceManagement.engagement.title': '{client}',
    'practiceManagement.engagement.openOrganisation': 'Open their organisation',
    'practiceManagement.engagement.portalNotInvited': 'This client cannot see tasks yet.',
    'practiceManagement.engagement.invitePortal': 'Invite them to the portal',
    // `portalPending` ("Portal invitation sent, not yet accepted") was removed on
    // 2026-08-14: it was a statement about the engagement, and an engagement can
    // now have two invitations outstanding at once. The condition belongs to the
    // entry — see `portalStateInvited`.
    'practiceManagement.engagement.tasksEmpty': 'No tasks yet.',
    'practiceManagement.engagement.taskOverdue': 'Overdue',
    'practiceManagement.engagement.documentReceived': 'Document received',
    'practiceManagement.engagement.documentAwaited': 'Waiting for document',

    'practiceManagement.engagement.loading': 'Loading this client…',
    'practiceManagement.engagement.error': 'We could not load this client. Try again.',
    'practiceManagement.engagement.notFound': 'This client could not be found. It may have been removed, or the link you followed may be out of date.',
    'practiceManagement.engagement.notFoundBackToClients': 'Back to your clients',
    'practiceManagement.engagement.denied': 'You do not have access to this client.',
    'practiceManagement.engagement.retry': 'Try again',
    'practiceManagement.engagement.untitled': 'Client not yet identified',
    'practiceManagement.engagement.backToClients': 'Back to clients',
    'practiceManagement.engagement.detailsHeading': 'Engagement',

    // The hub's tab rail (2026-08-14). `detailsHeading` above doubles as the
    // third tab's label — the panel it names is the engagement itself, and two
    // words for one thing is how a rail stops matching the panel it opens.
    'practiceManagement.engagement.filingsHeading': 'Filings',
    // The rail's own controls, none of which are read aloud on a wide screen:
    // the label belongs to the `<select>` that replaces the rail on a phone, and
    // the two chevrons carry no text of their own.
    'practiceManagement.engagement.chooseTab': 'Choose a page',
    'practiceManagement.engagement.scrollTabsStart': 'Scroll the tabs back',
    'practiceManagement.engagement.scrollTabsEnd': 'Scroll the tabs forward',
    'practiceManagement.engagement.filingsUnavailable': 'Filing obligations appear once this client has an organisation.',
    'practiceManagement.engagement.stateLabel': 'State',
    'practiceManagement.engagement.payerLabel': 'Payer',
    'practiceManagement.engagement.servicesLabel': 'Services',
    'practiceManagement.engagement.startedLabel': 'Started',
    'practiceManagement.engagement.endedLabel': 'Ended',
    'practiceManagement.engagement.contactLabel': 'Contact',
    'practiceManagement.engagement.reviewLabel': 'This client is waiting for your review.',
    'practiceManagement.engagement.notProvisioned': 'This client has no organisation yet, so there is nothing to open.',
    'practiceManagement.engagement.terminated': 'This engagement has ended. It is shown for reference only and cannot be changed.',

    'practiceManagement.engagement.portalHeading': 'Client portal',
    // `portalUnknown` ("We cannot show whether this client already has portal
    // access. Sending an invitation will tell you.") was removed on 2026-08-10:
    // it was written before `GET …/engagements/{id}/portal-users` existed, and
    // the panel now shows the real state through the four keys below.
    // `portalAccessGranted` ("{contact} has access to the client portal.") was
    // removed on 2026-08-14 with the single-contact sentence it wrote: the panel
    // is now a list, and each entry states its own condition through
    // `portalStateAccepted` / `portalStateInvited` below. A sentence about "the"
    // contact cannot be written once there may be three.
    'practiceManagement.engagement.portalNobodyYet': 'Nobody from this client has portal access yet.',
    'practiceManagement.engagement.portalReadDenied': 'You do not have permission to see whether this client already has portal access.',
    // AMENDED 2026-08-14: the trailing "Try again." went. The retry CONTROL sits
    // immediately beside this sentence and is labelled "Try again", so the
    // screen said it twice — once as an instruction the reader cannot act on in
    // the text, and once as the thing they actually press.
    'practiceManagement.engagement.portalReadFailed': 'We could not check whether this client already has portal access.',
    // A TYPED ADDRESS AGAIN (user ruling, 2026-08-14, superseding the bounded
    // picker of the same day). The six `portalInvitee*` keys that labelled the
    // picker — `portalInviteeLabel`, `portalInviteeChoose`,
    // `portalInviteeNobodyLeft`, `portalInviteeNone`, `portalInviteeDenied`,
    // `portalInviteeFailed` — are removed with it: a list of the client
    // organisation's existing members cannot reach the person who actually works
    // the books, who often has no account there at all.
    //
    // `portalInviteUnverified` ("This address is not checked. Whoever you send it
    // to will only be given access if they turn out to be part of this client's
    // organisation when they accept.") was added the same day and REMOVED by
    // user ruling a few hours later. The fact it stated is still true — nothing
    // about the address is checked when the invitation is sent — and the ruling
    // was taken with the sentence on screen, so it is not to come back as a
    // well-meant improvement.
    'practiceManagement.engagement.portalEmailLabel': 'Their e-mail address (leave blank to use the contact address on file)',
    'practiceManagement.engagement.portalInviteSent': 'Portal invitation sent to {email}.',
    'practiceManagement.engagement.portalInviteFailed': 'We could not send the portal invitation. Try again.',
    'practiceManagement.engagement.portalNotAvailable': 'A client can only be invited to the portal once their organisation has been set up.',
    // `portalPendingDetail` ("Sent to {email} on {date}.") and
    // `portalPendingSentTo` ("Sent to {email}.") went the same way on the same
    // day, and for the same reason as `portalPending` above them: an outstanding
    // invitation is now an entry in the list, which already carries the address
    // as its title, so both strings would have printed it twice.
    'practiceManagement.engagement.portalPendingReadFailed': 'We could not check whether a portal invitation is already outstanding for this client.',
    'practiceManagement.engagement.sendPortalInvitation': 'Send invitation',
    'practiceManagement.engagement.cancel': 'Cancel',

    // SEVERAL PEOPLE PER CLIENT (2026-08-14). The panel stops being a sentence
    // about one contact and becomes a list, so the copy above splits in two: the
    // per-entry state, and the actions that belong to an entry.
    'practiceManagement.engagement.portalStateAccepted': 'Has access',
    'practiceManagement.engagement.portalStateInvited': 'Invited, not yet accepted',
    'practiceManagement.engagement.portalInvitedOn': 'Invited on {date}.',

    // The invite control is no longer the empty state's only occupant, so it has
    // two labels: the first person, and the next one. One label for both would
    // read as an instruction to replace whoever is already listed.
    //
    // Neither the list nor the invite form takes a sub-heading of its own —
    // `ds_master_detail` allows one heading per panel, and the field's own label
    // already says what the form is for.
    'practiceManagement.engagement.portalInviteAnother': 'Invite someone else',

    // Withdrawal. The confirmation names the person and says what does NOT
    // happen — their tasks stay on the practice's board (`BR-04`) — because the
    // question a practice user hesitates over is whether they are about to lose
    // the work as well as the access.
    'practiceManagement.engagement.portalWithdraw': 'Withdraw',
    'practiceManagement.engagement.portalWithdrawNamed': 'Withdraw {contact}’s access',
    'practiceManagement.engagement.portalWithdrawConfirm': 'Withdraw {contact}’s access to the client portal?',
    'practiceManagement.engagement.portalWithdrawConfirmHelp': 'They will not be able to sign in to this client’s portal, and you can invite them again later.',

    // WHERE THEIR UNFINISHED WORK GOES (user ruling, 2026-08-14). Withdrawing
    // somebody used to leave their tasks assigned to an account that could no
    // longer open them — the work stayed on the board and nobody was coming for
    // it. The dialogue now asks, and "nobody" is a real answer rather than the
    // absence of one, which is why it is offered in words.
    //
    // Two count strings rather than one with a placeholder plural: this
    // catalogue has no plural machinery, and "1 unfinished tasks" is the kind of
    // sentence that makes a careful screen look careless.
    'practiceManagement.engagement.portalWithdrawTasksOne': 'One unfinished task is assigned to them.',
    'practiceManagement.engagement.portalWithdrawTasksMany': '{count} unfinished tasks are assigned to them.',
    'practiceManagement.engagement.portalWithdrawTasksHelp': 'Once their access is withdrawn they will not see these tasks. Choose who picks them up.',
    'practiceManagement.engagement.portalWithdrawTasksLabel': 'Give their unfinished tasks to',
    'practiceManagement.engagement.portalWithdrawTasksChoose': 'Choose who picks them up',
    // "Nobody" is a real answer and must be CHOSEN (user ruling, 2026-08-14): it
    // is no longer what the control shows before anybody has decided. The line
    // above is the unanswered state, and it cannot be selected.
    'practiceManagement.engagement.portalWithdrawTasksNobody': 'Nobody — leave them unassigned',
    // WHY access was withdrawn (2026-08-15). Deliberately worded to cover both
    // an accepted contact and an unaccepted invitation, because one dialogue asks
    // it in both states — "withdrawing it" would be wrong for a person and
    // "removing them" wrong for an invitation, so the question names neither.
    'practiceManagement.engagement.portalWithdrawReasonLabel': 'Why is this access being withdrawn?',
    'practiceManagement.engagement.portalWithdrawReasonHelp': 'Optional. This is kept with the record.',
    // The picker's empty-list sentence, overridden here because the list is
    // everyone EXCEPT the person being withdrawn. `assigneePortalNone`'s advice
    // — "invite them to the portal" — is for the task form, and reads as a
    // non-sequitur to somebody in the middle of taking access away.
    'practiceManagement.engagement.portalWithdrawTasksNobodyElse': 'Nobody else at this client has portal access, so their tasks can only go to a colleague or to nobody.',
    'practiceManagement.engagement.portalWithdrawTasksNone': 'They have no unfinished tasks.',
    'practiceManagement.engagement.portalWithdrawTasksFailed': 'We could not check whether they have unfinished tasks. If they have any, they will stay assigned to them and you can reassign them from the board.',
    'practiceManagement.engagement.portalWithdrawConfirmAction': 'Withdraw access',
    'practiceManagement.engagement.portalContactWithdrawn': '{contact} no longer has portal access.',
    'practiceManagement.engagement.portalWithdrawFailed': 'We could not withdraw their access. Try again.',

    // WITHDRAWING AN INVITATION IS NOT WITHDRAWING ACCESS (2026-08-14). The same
    // row and the same endpoint, and deliberately not the same words: somebody
    // who has never accepted has no access to take away, so the sentence above
    // would be a false statement about what is happening. What ends is the
    // asking — and what actually stops is the link, because acceptance requires
    // `revoked_at IS NULL` and a withdrawn token is answered as expired.
    //
    // The consequence differs too, which is why the help line is not shared:
    // there are no tasks to reassure the practice about, because a task cannot
    // be given to somebody who has not accepted.
    'practiceManagement.engagement.portalWithdrawInvitation': 'Withdraw invitation',
    'practiceManagement.engagement.portalWithdrawInvitationNamed': 'Withdraw the invitation to {contact}',
    'practiceManagement.engagement.portalWithdrawInvitationConfirm': 'Withdraw the invitation to {contact}?',
    'practiceManagement.engagement.portalWithdrawInvitationConfirmHelp': 'The link in their invitation will stop working, and they will not be able to accept it. You can invite them again later.',
    'practiceManagement.engagement.portalWithdrawInvitationConfirmAction': 'Withdraw invitation',
    'practiceManagement.engagement.portalInvitationWithdrawn': 'The invitation to {contact} has been withdrawn.',
    'practiceManagement.engagement.portalWithdrawInvitationFailed': 'We could not withdraw the invitation. Try again.',

    // The 409 for an address that is already live on THIS client. The server's
    // own message is shown verbatim when it sends one; this is what the panel
    // says when it cannot.
    'practiceManagement.engagement.portalInviteAddressAlreadyLive': 'That address already has access to this client’s portal, or an invitation outstanding.',

    // Said on an ACTIVE engagement with nobody live — including after the last
    // contact is withdrawn, which is the state that made it worth saying: a
    // return cannot be sent for approval to nobody, and the practice finds that
    // out here rather than at the moment they try to file.
    'practiceManagement.engagement.portalNobodyToApprove': 'Until somebody accepts, this client cannot be asked to approve a return.',

    'practiceManagement.engagement.tasksHeading': 'Tasks',
    'practiceManagement.engagement.tasksLoading': 'Loading tasks…',
    'practiceManagement.engagement.tasksError': 'We could not load the tasks. Try again.',
    'practiceManagement.engagement.tasksDenied': 'You do not have access to this client’s tasks.',
    'practiceManagement.engagement.newTask': 'New task',
    // `showArchived` ("Show archived tasks") was removed on 2026-08-10 with the
    // checkbox it labelled: the board's filter is now a three-state select, and a
    // catalogue entry nothing renders is a string a translator pays for twice.
    'practiceManagement.engagement.taskStatusFilterLabel': 'Showing',
    'practiceManagement.engagement.taskStatus.open': 'Open',
    'practiceManagement.engagement.taskStatus.completed': 'Completed',
    'practiceManagement.engagement.taskStatus.archived': 'Archived',
    'practiceManagement.engagement.taskDue': 'Due {date}',
    'practiceManagement.engagement.taskNoDueDate': 'No due date',
    'practiceManagement.engagement.taskUnassigned': 'Unassigned',
    'practiceManagement.engagement.taskAssigneeUnresolved': 'Assigned to someone no longer listed',
    'practiceManagement.engagement.documentCount': '{count} received',
    // What a task still wants, said on its board row (`FR-09`, 2026-08-13). The
    // message figure is a TOTAL, never an unread count — read state was decided
    // against (`NG-01`), so there is nothing an unread figure could be derived
    // from and a label implying one would be a lie.
    'practiceManagement.engagement.answeredOfTotal': '{answered} of {total} answered',
    'practiceManagement.engagement.messageCount': '{count} messages',
    'practiceManagement.engagement.showDocuments': 'Show documents',
    'practiceManagement.engagement.hideDocuments': 'Hide documents',
    'practiceManagement.engagement.documentsLoading': 'Loading documents…',
    'practiceManagement.engagement.documentsError': 'We could not load the documents. Try again.',
    'practiceManagement.engagement.documentUploadedBy': 'from {name}',
    'practiceManagement.engagement.downloadDocument': 'Download',
    'practiceManagement.engagement.documentDownloadFailed': 'We could not download “{name}”. Try again.',
    'practiceManagement.engagement.completeTask': 'Complete',
    'practiceManagement.engagement.editTask': 'Edit',
    'practiceManagement.engagement.archiveTask': 'Archive',
    'practiceManagement.engagement.taskCompleted': 'Task completed.',
    'practiceManagement.engagement.taskArchived': 'Task archived.',
    'practiceManagement.engagement.taskSaved': 'Task saved.',
    'practiceManagement.engagement.taskActionFailed': 'We could not update the task. Try again.',
    'practiceManagement.engagement.archiveReasonLabel': 'Why are you archiving this task?',
    'practiceManagement.engagement.archiveReasonHelp': 'Optional. This is kept with the task.',
    'practiceManagement.engagement.confirmArchive': 'Archive this task?',

    'practiceManagement.engagement.taskFormCreateTitle': 'New task',
    'practiceManagement.engagement.taskFormEditTitle': 'Edit task',
    'practiceManagement.engagement.taskTitleLabel': 'Title',
    'practiceManagement.engagement.taskDescriptionLabel': 'Description',
    'practiceManagement.engagement.taskDueLabel': 'Due date',
    'practiceManagement.engagement.taskDocumentRequestLabel': 'This task asks the client for a document',
    'practiceManagement.engagement.taskSave': 'Save task',
    'practiceManagement.engagement.taskFormError': 'We could not save the task. Try again.',

    // The edit slide-out refuses to render a form it could not fill (2026-08-14).
    // Both sentences say the task cannot be edited RIGHT NOW rather than that
    // nothing was found — an empty form here would invite a save that replaced
    // real questions with none.
    'practiceManagement.engagement.taskNotFound': 'We could not find that task. It may have been archived.',
    'practiceManagement.engagement.taskLoadFailed': 'We could not load this task. Close this panel and try again.',

    // The questions a task puts to the client (user ruling, 2026-08-13). A task
    // carries a SET of them, so every label here is written for a list rather
    // than for a single field.
    'practiceManagement.engagement.questionsHeading': 'Questions for the client',
    'practiceManagement.engagement.questionsHelp': 'The client answers these in their portal. A required question stops them marking the task done.',
    'practiceManagement.engagement.addQuestion': 'Add a question',
    'practiceManagement.engagement.questionCount': '{used} of {limit}',
    'practiceManagement.engagement.questionLabel': 'Question {position}',
    'practiceManagement.engagement.questionTypeLabel': 'Answer',
    'practiceManagement.engagement.questionRequiredLabel': 'Required',
    'practiceManagement.engagement.removeQuestion': 'Remove question {position}',
    'practiceManagement.engagement.questionAnswered': 'Answered — this question can no longer be changed or removed.',
    'practiceManagement.engagement.questionTypeText': 'Text',
    'practiceManagement.engagement.questionTypeDate': 'Date',
    'practiceManagement.engagement.questionTypeNumber': 'Number',
    'practiceManagement.engagement.questionTypeYesNo': 'Yes / No',

    'practiceManagement.engagement.assigneeLabel': 'Assign to',
    'practiceManagement.engagement.assigneeNobody': 'Nobody yet',
    'practiceManagement.engagement.assigneeYourTeam': 'Your team',
    'practiceManagement.engagement.assigneeThisClient': 'This client',
    'practiceManagement.engagement.assigneePortalSuffix': '{name} (portal)',
    'practiceManagement.engagement.assigneeTeamUnavailable': 'We cannot list your colleagues — you do not have permission to read your organisation’s members.',
    'practiceManagement.engagement.assigneeTeamFailed': 'We could not load your colleagues. Close this and try again.',
    // AMENDED 2026-08-10 — `assigneePortalUnavailable` used to read “This client’s
    // portal contact cannot be listed until they have been given a task.”, which
    // described the picker's old source (the engagement's own tasks) and is no
    // longer true of anything: the contacts are read from the engagement itself.
    // The three sentences below are the three answers that read is now capable of
    // giving, and they are deliberately different from each other — “we could not
    // find out” is not “there is nobody”.
    'practiceManagement.engagement.assigneePortalUnavailable': 'We cannot list this client’s portal contact — you do not have permission to read it.',
    'practiceManagement.engagement.assigneePortalFailed': 'We could not load this client’s portal contact. Close this and try again.',
    'practiceManagement.engagement.assigneePortalNone': 'This client has no portal contact yet. Invite them to the portal, and they can be given tasks once they accept.',
    'practiceManagement.engagement.assigneeCannotBeCleared': 'An assignee cannot be removed once one is set. Choose someone else instead.',
};
